import { type ReactElement, useEffect, useLayoutEffect, useRef } from 'react';

import {
  long5,
  longest2,
  longest3,
  short2,
} from '@spotify-internal/encore-foundation';
import { Text } from '@spotify-internal/encore-web';

import styles from './AnimatedLoginTitle.module.css';

const TITLE_LINES = [
  'Have an idea.',
  'Test it. Ship it.',
  'With Confidence.',
] as const;

const SCRAMBLE_UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const SCRAMBLE_LOWER = 'abcdefghijklmnopqrstuvwxyz';
const CURSOR_CHARS = '░▒▓█';
const milliseconds = (duration: string): number =>
  Number.parseInt(duration, 10);
const LINE_DURATION_MS = milliseconds(longest2);
const SETTLE_DURATION_MS = milliseconds(short2);
const SETTLE_FRACTION = SETTLE_DURATION_MS / LINE_DURATION_MS;
const LINE_STAGGER_MS = LINE_DURATION_MS;
const LOOP_DELAY_MS = milliseconds(longest3) * 2;
const FRAMES_PER_SECOND = 30;
const FRAME_INTERVAL_MS = milliseconds(long5) / FRAMES_PER_SECOND;

const TOTAL_ANIM_MS =
  LINE_DURATION_MS + (TITLE_LINES.length - 1) * LINE_STAGGER_MS;

type CharacterSlotRefs = Array<Array<HTMLSpanElement | null>>;

const settleTitle = (characterSlotRefs: CharacterSlotRefs): void => {
  TITLE_LINES.forEach((line, lineIndex) => {
    Array.from(line).forEach((char, charIndex) => {
      const slot = characterSlotRefs[lineIndex][charIndex];
      if (slot) slot.textContent = char;
    });
  });
};

const scrambleLine = (
  line: string,
  progressNorm: number,
  frameIndex: number,
): string => {
  const chars = Array.from(line);
  const N = chars.length;
  const staggerWidth = (1 - SETTLE_FRACTION) / N;
  const cursorZoneWidth = CURSOR_CHARS.length * staggerWidth;

  return chars
    .map((char, i) => {
      if (char === ' ') return ' ';
      if (!/[A-Za-z]/.test(char)) return char;

      const K = (i / Math.max(N - 1, 1)) * (1 - SETTLE_FRACTION);
      const G = K + SETTLE_FRACTION;

      if (progressNorm >= G) return char;

      const windowProgress = progressNorm - K;
      if (windowProgress > 0 && windowProgress < cursorZoneWidth) {
        const cursorIdx = Math.min(
          Math.floor(windowProgress / staggerWidth),
          CURSOR_CHARS.length - 1,
        );
        return CURSOR_CHARS[CURSOR_CHARS.length - 1 - cursorIdx];
      }

      const pool =
        char === char.toUpperCase() ? SCRAMBLE_UPPER : SCRAMBLE_LOWER;
      return pool.charAt((frameIndex * 7 + i * 3) % pool.length);
    })
    .join('');
};

export const AnimatedLoginTitle = (): ReactElement => {
  const charSlotRefs = useRef<Array<Array<HTMLSpanElement | null>>>(
    TITLE_LINES.map(line => Array.from({ length: line.length }, () => null)),
  );

  useLayoutEffect(() => {
    const measureCharacterSlots = (): void => {
      const characterSlots = charSlotRefs.current
        .flat()
        .filter((slot): slot is HTMLSpanElement => slot !== null);
      const displayedCharacters = characterSlots.map(slot => slot.textContent);

      settleTitle(charSlotRefs.current);
      characterSlots.forEach(slot => {
        slot.style.width = 'auto';
      });
      const characterWidths = characterSlots.map(
        slot => slot.getBoundingClientRect().width,
      );
      characterSlots.forEach((slot, index) => {
        slot.style.width = `${characterWidths[index]}px`;
        slot.textContent = displayedCharacters[index];
      });
    };

    let resizeAnimationFrameId: number | undefined;
    const scheduleMeasure = (): void => {
      if (resizeAnimationFrameId !== undefined) return;

      resizeAnimationFrameId = window.requestAnimationFrame(() => {
        resizeAnimationFrameId = undefined;
        measureCharacterSlots();
      });
    };

    let isMounted = true;
    measureCharacterSlots();
    window.addEventListener('resize', scheduleMeasure);
    void document.fonts?.ready.then(() => {
      if (isMounted) measureCharacterSlots();
    });

    return () => {
      isMounted = false;
      window.removeEventListener('resize', scheduleMeasure);
      if (resizeAnimationFrameId !== undefined) {
        window.cancelAnimationFrame(resizeAnimationFrameId);
      }
    };
  }, []);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    let animationFrameId: number | undefined;
    let pauseTimeoutId: number | undefined;

    const updateTitle = (
      elapsedMilliseconds: number,
      frameIndex: number,
    ): void => {
      TITLE_LINES.forEach((line, lineIndex) => {
        const lineElapsed = Math.max(
          elapsedMilliseconds - lineIndex * LINE_STAGGER_MS,
          0,
        );
        const progressNorm = Math.min(lineElapsed / LINE_DURATION_MS, 1);
        const result = scrambleLine(line, progressNorm, frameIndex);

        Array.from(result).forEach((char, charIndex) => {
          const slot = charSlotRefs.current[lineIndex][charIndex];
          if (slot) slot.textContent = char;
        });
      });
    };

    const startCycle = (): void => {
      let startTime: number | undefined;
      let lastFrameTime = -Infinity;
      let frameIndex = 0;

      const animate = (timestamp: number): void => {
        startTime ??= timestamp;
        const elapsedMilliseconds = timestamp - startTime;

        if (elapsedMilliseconds >= TOTAL_ANIM_MS) {
          settleTitle(charSlotRefs.current);
          pauseTimeoutId = window.setTimeout(startCycle, LOOP_DELAY_MS);
          return;
        }

        if (timestamp - lastFrameTime >= FRAME_INTERVAL_MS) {
          lastFrameTime = timestamp;
          frameIndex++;
          updateTitle(elapsedMilliseconds, frameIndex);
        }

        animationFrameId = window.requestAnimationFrame(animate);
      };

      animationFrameId = window.requestAnimationFrame(animate);
    };

    startCycle();

    return () => {
      if (animationFrameId !== undefined) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (pauseTimeoutId !== undefined) {
        window.clearTimeout(pauseTimeoutId);
      }
    };
  }, []);

  return (
    <Text
      as="h1"
      variant="titleLarge"
      className={styles.root}
      aria-label={TITLE_LINES.join(' ')}
    >
      {TITLE_LINES.map((line, lineIndex) => (
        <span key={line} className={styles.lineWrapper} aria-hidden="true">
          {Array.from(line).map((char, charIndex) =>
            char === ' ' ? (
              <span
                key={charIndex}
                className={styles.spaceSlot}
                aria-hidden="true"
              >
                {'\u00a0'}
              </span>
            ) : (
              <span
                key={charIndex}
                className={styles.charSlot}
                ref={el => {
                  charSlotRefs.current[lineIndex][charIndex] = el;
                }}
              >
                {char}
              </span>
            ),
          )}
        </span>
      ))}
    </Text>
  );
};
